import { useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  extractPackagingList,
  commitPackagingList,
  listPackagingLists,
  labelsPdfUrl,
  packagingLabelsTsplUrl,
  printTsplViaBridge,
  isBridgeAlive,
  bridgeDownloadUrl,
  listMasters,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LabelPrinterStatus from "@/components/LabelPrinterStatus";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  UploadCloud, FileText, Loader2, Trash2, Printer, Sparkles, Plus, ArrowRight, Layers,
} from "lucide-react";
import { SfgCountDialog } from "@/components/SfgCountDialog";
import { DeletePackingListDialog } from "@/components/DeletePackingListDialog";
import { useAuth } from "@/contexts/AuthContext";

const EditableCell = ({ value, onChange, type = "text", testId, step = "0.01", min }) => (
  <Input
    data-testid={testId}
    type={type}
    value={value ?? ""}
    step={type === "number" ? step : undefined}
    min={min}
    onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
    className="h-9 font-mono-data"
  />
);

const PackagingLists = () => {
  const [sfgCountPkg, setSfgCountPkg] = useState(null);
  const [deletePkg, setDeletePkg] = useState(null);
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const [pdfFiles, setPdfFiles] = useState([]);
  const [drafts, setDrafts] = useState([]); // array of draft objects, one per file
  const [filters, setFilters] = useState({ rm_tag_creation_id: "", supplier_name: "", date_of_dispatch: "" });
  const [entryKind, setEntryKind] = useState("mother");   // "mother" (RM) | "sfg"
  const [showManual, setShowManual] = useState(false);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  // Full unfiltered list for populating dropdowns
  const { data: allLists = [] } = useQuery({
    queryKey: ["packaging-lists", "all"],
    queryFn: () => listPackagingLists(),
  });

  const { data: lists = [] } = useQuery({
    queryKey: ["packaging-lists", filters],
    queryFn: () => listPackagingLists(filters),
  });

  const supplierOptions = useMemo(
    () => Array.from(new Set(allLists.map((p) => p.supplier_name).filter(Boolean))).sort(),
    [allLists]
  );
  const tagIdOptions = useMemo(
    () => allLists.map((p) => p.rm_tag_creation_id).filter(Boolean).sort((a, b) => {
      const na = parseInt(a.replace(/\D/g, ""), 10) || 0;
      const nb = parseInt(b.replace(/\D/g, ""), 10) || 0;
      return nb - na;
    }),
    [allLists]
  );
  const dateOptions = useMemo(
    () => Array.from(new Set(allLists.map((p) => p.date_of_dispatch).filter(Boolean))).sort().reverse(),
    [allLists]
  );
  const [bridgeAlive, setBridgeAlive] = useState(null);
  // Ping the local print bridge once on mount + when filters change (no polling)
  useMemo(() => { isBridgeAlive().then(setBridgeAlive); return null; }, []);

  const doDirectPrint = async (pkgId, pkgNumber) => {
    if (bridgeAlive === false) {
      toast.error("Print Bridge not running on this PC. Download the setup kit and double-click Install_Autostart.bat once — it will auto-start every login after that.", {
        action: { label: "Download setup kit", onClick: () => window.open(bridgeDownloadUrl(), "_blank") },
        duration: 9000,
      });
      return;
    }
    const t = toast.loading(`Sending ${pkgNumber} to printer…`);
    try {
      await printTsplViaBridge(packagingLabelsTsplUrl(pkgId));
      toast.dismiss(t);
      toast.success(`Printed labels for ${pkgNumber}`);
      setBridgeAlive(true);
    } catch (e) {
      toast.dismiss(t);
      toast.error(`Print failed: ${e?.message || "bridge unreachable"}`, {
        action: { label: "Download setup kit", onClick: () => window.open(bridgeDownloadUrl(), "_blank") },
      });
      setBridgeAlive(false);
    }
  };

  const filtersActive = filters.rm_tag_creation_id || filters.supplier_name || filters.date_of_dispatch;

  const extractMut = useMutation({
    mutationFn: async (files) => {
      const results = await Promise.all(files.map(async (f) => {
        try {
          const data = await extractPackagingList(f);
          return { filename: f.name, ok: true, data };
        } catch (e) {
          return { filename: f.name, ok: false, error: e?.response?.data?.detail || e.message };
        }
      }));
      return results;
    },
    onSuccess: (results) => {
      const newDrafts = results.filter((r) => r.ok).map((r, di) => ({
        _uid: `draft-${Date.now()}-${di}`,
        _filename: r.filename,
        kind: entryKind,
        supplier_name: r.data.supplier_name || "",
        packaging_list_number: r.data.packaging_list_number || "",
        vehicle_number: r.data.vehicle_number || "",
        date_of_dispatch: r.data.date_of_dispatch || new Date().toISOString().slice(0, 10),
        coils: (r.data.coils || []).map((c, ri) => ({
          _uid: `coil-${Date.now()}-${di}-${ri}`,
          grade: c.grade || "",
          grade_raw: c.grade_raw || "",  // when AI normalised it from a different source string
          thickness_mm: c.thickness_mm ?? "",
          width_mm: c.width_mm ?? "",
          net_weight_kg: c.net_weight_kg ?? "",
          gross_weight_kg: c.gross_weight_kg ?? "",
          supplier_coil_id: c.supplier_coil_id || "",
          sfg_count: c.sfg_count ?? 1,
        })),
      }));
      const failed = results.filter((r) => !r.ok);
      setDrafts(newDrafts);
      if (newDrafts.length) toast.success(`Extracted ${newDrafts.length} file(s)`);
      failed.forEach((f) => toast.error(`${f.filename}: ${f.error}`));
    },
    onError: (e) => toast.error(e?.message || "Extraction failed"),
  });

  const commitMut = useMutation({
    mutationFn: (vars) => commitPackagingList(vars.payload),
    onSuccess: (data, vars) => {
      toast.success(`Committed ${data.coils.length} ${data.packaging_list.kind === "sfg" ? "SFG" : "RM coils"} · ${data.packaging_list.rm_tag_creation_id}`);
      setDrafts((arr) => arr.filter((_, i) => i !== vars.draftIdx));
      queryClient.invalidateQueries({ queryKey: ["packaging-lists"] });
      queryClient.invalidateQueries({ queryKey: ["coils"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      window.open(labelsPdfUrl(data.packaging_list.id), "_blank");
    },
    onError: (e, vars) => {
      const raw = e?.response?.data?.detail;
      const is409 = e?.response?.status === 409;
      // Duplicate: backend returns detail = {message, packaging_list_id, rm_tag_creation_id, ...}
      if (is409 && raw && typeof raw === "object" && raw.rm_tag_creation_id) {
        const tag = raw.rm_tag_creation_id;
        const openLabels = () => {
          window.open(labelsPdfUrl(raw.packaging_list_id), "_blank");
          toast.dismiss();
        };
        // Drop the local draft — this exact list is unusable now.
        if (vars?.draftIdx != null) {
          setDrafts((arr) => arr.filter((_, i) => i !== vars.draftIdx));
        }
        toast.warning(
          `Packaging list already scanned and committed to ${tag} — this exact packaging list can't be committed twice. You can reprint the same labels.`,
          {
            duration: 12000,
            action: { label: "Reprint labels", onClick: openLabels },
            id: `duplicate-${tag}`,
          }
        );
        return;
      }
      toast.error(typeof raw === "string" ? raw : (raw?.message || e.message || "Commit failed"));
    },
  });

  const onFileChange = (e) => {
    const fs = Array.from(e.target.files || []);
    if (!fs.length) return;
    const ACCEPTED = [".pdf", ".xlsx", ".xls", ".csv", ".docx"];
    const bad = fs.find((f) => !ACCEPTED.some((ext) => f.name.toLowerCase().endsWith(ext)));
    if (bad) {
      toast.error(`${bad.name} — please upload PDF, Excel or Word (.docx).`);
      return;
    }
    setPdfFiles(fs);
    setDrafts([]);
  };

  const handleExtract = () => {
    if (!pdfFiles.length) return;
    extractMut.mutate(pdfFiles);
  };

  const updateDraft = (di, k, v) =>
    setDrafts((arr) => arr.map((d, i) => (i === di ? { ...d, [k]: v } : d)));
  const updateRow = (di, ri, k, v) =>
    setDrafts((arr) => arr.map((d, i) => (
      i === di ? { ...d, coils: d.coils.map((c, j) => (j === ri ? { ...c, [k]: v } : c)) } : d
    )));
  const removeRow = (di, ri) =>
    setDrafts((arr) => arr.map((d, i) => (
      i === di ? { ...d, coils: d.coils.filter((_, j) => j !== ri) } : d
    )));
  const addRow = (di) =>
    setDrafts((arr) => arr.map((d, i) => (
      i === di ? { ...d, coils: [...d.coils, { _uid: `coil-${Date.now()}-${Math.random()}`, grade: "", thickness_mm: "", width_mm: "", net_weight_kg: "", gross_weight_kg: "", supplier_coil_id: "", sfg_count: 1 }] } : d
    )));
  const removeDraft = (di) => {
    const next = drafts.filter((_, i) => i !== di);
    setDrafts(next);
    // If this was the last draft, also clear the file-picker state so the upload area
    // returns to its empty "Choose PDF files" state and the same file can be re-selected.
    if (next.length === 0) {
      setPdfFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const canCommitDraft = (d) =>
    d &&
    d.supplier_name.trim() &&
    d.packaging_list_number.trim() &&
    d.date_of_dispatch &&
    d.coils.length > 0 &&
    d.coils.every((c) => c.grade && Number(c.thickness_mm) > 0 && Number(c.width_mm) > 0 && Number(c.net_weight_kg) > 0
      && (d.kind !== "sfg" || (Number.isInteger(Number(c.sfg_count ?? 1)) && Number(c.sfg_count ?? 1) >= 1 && Number(c.sfg_count ?? 1) <= 999)));

  const handleCommit = (di) => {
    const d = drafts[di];
    if (!canCommitDraft(d)) return;
    commitMut.mutate({
      draftIdx: di,
      payload: {
        supplier_name: d.supplier_name.trim(),
        packaging_list_number: d.packaging_list_number.trim(),
        vehicle_number: d.vehicle_number.trim(),
        date_of_dispatch: d.date_of_dispatch,
        kind: d.kind || "mother",
        coils: d.coils.map((c) => ({
          grade: c.grade,
          thickness_mm: Number(c.thickness_mm),
          width_mm: Number(c.width_mm),
          net_weight_kg: Number(c.net_weight_kg),
          gross_weight_kg: c.gross_weight_kg === "" || c.gross_weight_kg == null ? null : Number(c.gross_weight_kg),
          supplier_coil_id: c.supplier_coil_id || "",
          sfg_count: Math.max(1, parseInt(c.sfg_count, 10) || 1),
        })),
      },
    });
  };

  return (
    <div data-testid="packaging-lists-page" className="space-y-6">
      <div className="border-b border-slate-200 pb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-1">Pre-arrival</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">RM / SFG Coil Tags</h2>
          <p className="text-sm text-slate-600 mt-1">
            Upload the supplier&apos;s packaging list (PDF / Excel / Word) OR enter the coils manually when the supplier didn&apos;t
            send one. Toggle whether this is a <b>Raw-Material</b> or <b>Semi-Finished-Goods</b> purchase — the ERP generates
            the matching SRE RM or SRE SFG IDs and prints QR labels ready for inward.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* RM / SFG toggle — controls which ID sequence commits will use */}
          <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden" role="tablist" aria-label="Coil kind">
            <button
              data-testid="kind-toggle-mother"
              type="button"
              onClick={() => setEntryKind("mother")}
              className={`px-3 h-9 text-xs font-semibold tracking-wide ${entryKind === "mother" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            >
              RM (mother coil)
            </button>
            <button
              data-testid="kind-toggle-sfg"
              type="button"
              onClick={() => setEntryKind("sfg")}
              className={`px-3 h-9 text-xs font-semibold tracking-wide border-l border-slate-300 ${entryKind === "sfg" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            >
              SFG (semi-finished)
            </button>
          </div>
          <Button
            data-testid="add-manual-btn"
            variant="outline"
            onClick={() => setShowManual(true)}
            className="h-9"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add manually
          </Button>
          <Link
            to="/inventory?kind=mother"
            data-testid="view-rm-in-inventory-pkg"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 border border-slate-300 bg-slate-50 hover:bg-slate-100 rounded-sm px-3 py-2 transition-colors"
          >
            View all in Inventory <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {/* Upload card */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 sm:p-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Step 1</p>
            <h3 className="font-heading text-lg font-bold text-slate-900 mt-0.5">Upload for AI extraction</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Accepted: PDF · Excel (.xlsx, .xls, .csv) · Word (.docx)</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label
              htmlFor="pdf-upload"
              data-testid="pdf-dropzone"
              className="inline-flex items-center gap-2 border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 px-4 h-10 rounded-sm cursor-pointer text-sm font-medium text-slate-700 transition-colors"
            >
              <UploadCloud className="h-4 w-4" />
              {pdfFiles.length === 0 ? "Choose file(s)" : pdfFiles.length === 1 ? pdfFiles[0].name : `${pdfFiles.length} files selected`}
            </label>
            <input
              id="pdf-upload"
              ref={fileInputRef}
              type="file"
              accept="application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,.pdf,.xlsx,.xls,.csv,.docx"
              multiple
              className="hidden"
              data-testid="pdf-file-input"
              onChange={onFileChange}
            />
            <Button
              onClick={handleExtract}
              disabled={pdfFiles.length === 0 || extractMut.isPending}
              data-testid="extract-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm h-10"
            >
              {extractMut.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Extracting {pdfFiles.length}…</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" /> Extract with AI</>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Review drafts */}
      {drafts.map((draft, di) => (
        <div key={draft._uid} data-testid={`draft-review-${di}`} className="bg-white border border-slate-200 rounded-sm p-5 sm:p-6 space-y-5">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Draft {di + 1} / {drafts.length}{draft._filename ? ` · ${draft._filename}` : ""}</p>
              <h3 className="font-heading text-lg font-bold text-slate-900 mt-0.5">Review &amp; edit before commit</h3>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => removeDraft(di)} data-testid={`discard-draft-${di}`} className="text-slate-500 h-10 rounded-sm">
                Discard
              </Button>
              <Button variant="outline" onClick={() => addRow(di)} data-testid={`add-row-btn-${di}`} className="rounded-sm h-10">
                <Plus className="h-4 w-4 mr-2" /> Add row
              </Button>
              <Button
                onClick={() => handleCommit(di)}
                disabled={!canCommitDraft(draft) || commitMut.isPending}
                data-testid={`commit-btn-${di}`}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-10"
              >
                {commitMut.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Committing…</> : <><Printer className="h-4 w-4 mr-2" /> Commit &amp; print</>}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Supplier</Label>
              <Input data-testid={`draft-supplier-name-${di}`} value={draft.supplier_name} onChange={(e) => updateDraft(di, "supplier_name", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Packaging List #</Label>
              <Input data-testid={`draft-packaging-list-number-${di}`} value={draft.packaging_list_number} onChange={(e) => updateDraft(di, "packaging_list_number", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Vehicle Number</Label>
              <Input data-testid={`draft-vehicle-number-${di}`} value={draft.vehicle_number} onChange={(e) => updateDraft(di, "vehicle_number", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Date of Dispatch</Label>
              <Input data-testid={`draft-date-of-dispatch-${di}`} type="date" value={draft.date_of_dispatch} onChange={(e) => updateDraft(di, "date_of_dispatch", e.target.value)} />
            </div>
          </div>

          <div className="overflow-x-auto border border-slate-200 rounded-sm">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">#</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Grade</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Thk (mm)</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Width (mm)</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Net Wt</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Gross Wt</TableHead>
                  <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Supplier Coil</TableHead>
                  {draft.kind === "sfg" && <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500" title="From the 'No of sheets' column — how many SFGs this row holds">No of SFGs</TableHead>}
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {draft.coils.map((c, ri) => (
                  <TableRow key={c._uid} data-testid={`draft-row-${di}-${ri}`}>
                    <TableCell className="text-slate-500 font-mono-data text-xs">{ri + 1}</TableCell>
                    <TableCell>
                      <EditableCell testId={`draft-grade-${di}-${ri}`} value={c.grade} onChange={(v) => updateRow(di, ri, "grade", v)} />
                      {c.grade_raw && (
                        <p data-testid={`draft-grade-raw-${di}-${ri}`} className="text-[10px] text-amber-700 mt-0.5 font-mono truncate max-w-[140px]" title={`AI normalised "${c.grade_raw}" → "${c.grade}"`}>
                          AI ← {c.grade_raw}
                        </p>
                      )}
                    </TableCell>
                    <TableCell><EditableCell testId={`draft-thickness-${di}-${ri}`} value={c.thickness_mm} type="number" onChange={(v) => updateRow(di, ri, "thickness_mm", v)} /></TableCell>
                    <TableCell><EditableCell testId={`draft-width-${di}-${ri}`} value={c.width_mm} type="number" onChange={(v) => updateRow(di, ri, "width_mm", v)} /></TableCell>
                    <TableCell><EditableCell testId={`draft-net-weight-${di}-${ri}`} value={c.net_weight_kg} type="number" onChange={(v) => updateRow(di, ri, "net_weight_kg", v)} /></TableCell>
                    <TableCell><EditableCell testId={`draft-gross-weight-${di}-${ri}`} value={c.gross_weight_kg} type="number" onChange={(v) => updateRow(di, ri, "gross_weight_kg", v)} /></TableCell>
                    <TableCell><EditableCell testId={`draft-supplier-coil-${di}-${ri}`} value={c.supplier_coil_id} onChange={(v) => updateRow(di, ri, "supplier_coil_id", v)} /></TableCell>
                    {draft.kind === "sfg" && (
                      <TableCell><EditableCell testId={`draft-sfg-count-${di}-${ri}`} value={c.sfg_count} type="number" step="1" min="1" onChange={(v) => updateRow(di, ri, "sfg_count", v)} /></TableCell>
                    )}
                    <TableCell>
                      <button data-testid={`remove-row-${di}-${ri}`} onClick={() => removeRow(di, ri)} className="text-slate-400 hover:text-red-600 p-1" aria-label="Remove row">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}

      {/* Existing packaging lists */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">History</p>
            <h3 className="font-heading text-lg font-bold text-slate-900 mt-0.5">Previously committed packaging lists</h3>
            <div className="mt-1.5" data-testid="bridge-status">
              <LabelPrinterStatus testId="packaging-label-printer-status" />
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2" data-testid="pl-filters">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">RM / SFG Tag ID</label>
              <select
                data-testid="filter-rm-tag"
                value={filters.rm_tag_creation_id}
                onChange={(e) => setFilters((f) => ({ ...f, rm_tag_creation_id: e.target.value }))}
                className="mt-0.5 block h-9 px-2 border border-slate-300 rounded-sm text-xs font-mono bg-white min-w-[130px]"
              >
                <option value="">All</option>
                {tagIdOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Supplier</label>
              <select
                data-testid="filter-supplier"
                value={filters.supplier_name}
                onChange={(e) => setFilters((f) => ({ ...f, supplier_name: e.target.value }))}
                className="mt-0.5 block h-9 px-2 border border-slate-300 rounded-sm text-xs bg-white min-w-[180px]"
              >
                <option value="">All</option>
                {supplierOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Supplier Dispatch Date</label>
              <select
                data-testid="filter-dispatch-date"
                value={filters.date_of_dispatch}
                onChange={(e) => setFilters((f) => ({ ...f, date_of_dispatch: e.target.value }))}
                className="mt-0.5 block h-9 px-2 border border-slate-300 rounded-sm text-xs font-mono bg-white min-w-[140px]"
              >
                <option value="">All</option>
                {dateOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            {filtersActive && (
              <button
                onClick={() => setFilters({ rm_tag_creation_id: "", supplier_name: "", date_of_dispatch: "" })}
                data-testid="filter-clear"
                className="h-9 px-3 text-xs font-semibold tracking-wider uppercase text-slate-600 hover:text-slate-900 border border-slate-300 bg-slate-50 hover:bg-slate-100 rounded-sm"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        {lists.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center">
            {filtersActive ? "No packaging lists match your filters." : "No packaging lists yet."}
          </p>
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Tag</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Supplier</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Pack List #</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Vehicle</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Dispatch Date</TableHead>
                <TableHead className="text-right text-[10px] tracking-[0.2em] uppercase text-slate-500">Coils</TableHead>
                <TableHead className="text-right text-[10px] tracking-[0.2em] uppercase text-slate-500">Labels</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lists.map((p) => (
                <TableRow key={p.id} data-testid={`packaging-row-${p.packaging_list_number}`}>
                  <TableCell className="font-mono-data text-xs">{p.rm_tag_creation_id || "—"}</TableCell>
                  <TableCell className="font-semibold">{p.supplier_name}</TableCell>
                  <TableCell className="font-mono-data">{p.packaging_list_number}</TableCell>
                  <TableCell>{p.vehicle_number || "—"}</TableCell>
                  <TableCell>{p.date_of_dispatch}</TableCell>
                  <TableCell className="text-right font-mono-data">{p.coil_count}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      <button
                        onClick={() => doDirectPrint(p.id, p.packaging_list_number)}
                        data-testid={`direct-print-${p.packaging_list_number}`}
                        title={bridgeAlive ? "One-click print to SRE_Label_1" : "Print Bridge helper needed on this PC"}
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm border ${
                          bridgeAlive ? "bg-emerald-50 hover:bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-slate-50 hover:bg-slate-100 border-slate-300 text-slate-600"
                        }`}
                      >
                        <Printer className="h-3.5 w-3.5" /> Print
                      </button>
                      <a
                        href={labelsPdfUrl(p.id)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`open-labels-${p.packaging_list_number}`}
                        className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 text-sm font-semibold"
                      >
                        <FileText className="h-4 w-4" /> PDF
                      </a>
                      {p.kind === "sfg" && (
                        <button onClick={() => setSfgCountPkg(p)} data-testid={`sfg-count-update-${p.packaging_list_number}`}
                          title="Re-upload this packing list to fill in No of SFGs"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm border bg-orange-50 hover:bg-orange-100 border-orange-300 text-orange-800">
                          <Layers className="h-3.5 w-3.5" /> No of SFGs
                        </button>
                      )}
                      {isDirector && (
                        <button onClick={() => setDeletePkg(p)} data-testid={`delete-pkg-${p.packaging_list_number}`} title="Delete this packing list (Director)"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm border bg-white hover:bg-red-50 border-red-300 text-red-700">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {sfgCountPkg && (
        <SfgCountDialog pkg={sfgCountPkg} onClose={() => setSfgCountPkg(null)} onPrint={() => doDirectPrint(sfgCountPkg.id, sfgCountPkg.packaging_list_number)} />
      )}
      {deletePkg && <DeletePackingListDialog pkg={deletePkg} onClose={() => setDeletePkg(null)} />}

      {/* Manual entry (supplier didn't send a packaging list) */}
      {showManual && (
        <ManualEntryDialog
          kind={entryKind}
          onCancel={() => setShowManual(false)}
          onCreate={(draft) => {
            setDrafts((arr) => [...arr, draft]);
            setShowManual(false);
            toast.success("Draft added — review below then Commit to generate IDs.");
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Manual entry dialog — creates a draft with all the fields we normally
// extract from a supplier's packaging list. On save, the draft appears in the
// same review table (the user can still tweak grades / weights before Commit).
// ---------------------------------------------------------------------------
const ManualEntryDialog = ({ kind, onCancel, onCreate }) => {
  // Load suppliers + material master (RM master for mother coils, SFG master for SFG).
  const materialType = kind === "sfg" ? "sfg_sku" : "rm_grade";
  const { data: suppliers = [] } = useQuery({
    queryKey: ["masters", "supplier"],
    queryFn: () => listMasters({ master_type: "supplier" }),
    staleTime: 30_000,
  });
  const { data: materials = [] } = useQuery({
    queryKey: ["masters", materialType],
    queryFn: () => listMasters({ master_type: materialType }),
    staleTime: 30_000,
  });
  const activeSuppliers = useMemo(
    () => suppliers.filter((s) => s.active !== false && s.status === "active"),
    [suppliers],
  );
  const activeMaterials = useMemo(
    () => materials.filter((m) => m.active !== false && m.status === "active"),
    [materials],
  );

  const [meta, setMeta] = useState({
    supplier_name: "",
    packaging_list_number: "",
    vehicle_number: "",
    date_of_dispatch: new Date().toISOString().slice(0, 10),
  });
  const emptyRow = () => ({
    material_id: "",
    grade: "",
    thickness_mm: "",
    width_mm: "",
    net_weight_kg: "",
    gross_weight_kg: "",
    supplier_coil_id: "",
    sfg_count: 1,
  });
  const [rows, setRows] = useState([emptyRow()]);

  const canSave =
    meta.supplier_name.trim() &&
    meta.packaging_list_number.trim() &&
    meta.date_of_dispatch &&
    rows.length > 0 &&
    rows.every((c) => c.grade && Number(c.thickness_mm) > 0 && Number(c.width_mm) > 0 && Number(c.net_weight_kg) > 0);

  const updRow = (i, k, v) => setRows((arr) => arr.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const pickMaterial = (i, mid) => {
    const mat = activeMaterials.find((m) => m.id === mid);
    if (!mat) {
      updRow(i, "material_id", "");
      return;
    }
    const mm = mat.meta || {};
    setRows((arr) =>
      arr.map((r, idx) =>
        idx === i
          ? {
              ...r,
              material_id: mid,
              grade: mm.grade != null ? String(mm.grade) : r.grade,
              thickness_mm: mm.thickness_mm != null ? String(mm.thickness_mm) : r.thickness_mm,
              width_mm: mm.width_mm != null ? String(mm.width_mm) : r.width_mm,
            }
          : r,
      ),
    );
  };

  const addRow = () => setRows((arr) => [...arr, emptyRow()]);
  const rmRow  = (i) => setRows((arr) => arr.filter((_, idx) => idx !== i));

  const save = () => {
    onCreate({
      _uid: `manual-${Date.now()}`,
      _filename: `${kind === "sfg" ? "SFG" : "RM"} · manual entry`,
      kind,
      supplier_name: meta.supplier_name.trim(),
      packaging_list_number: meta.packaging_list_number.trim(),
      vehicle_number: meta.vehicle_number.trim(),
      date_of_dispatch: meta.date_of_dispatch,
      coils: rows.map(({ material_id, ...rest }) => rest),
    });
  };

  return (
    <div onClick={onCancel} className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="manual-entry-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl bg-white rounded-sm border border-slate-200 mt-8">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">
              Manual coil entry
            </p>
            <h3 className="font-heading text-lg font-bold text-slate-900">
              {kind === "sfg" ? "SFG (semi-finished goods)" : "RM (mother coil)"} — no packaging list
            </h3>
          </div>
          <Button variant="ghost" size="icon" onClick={onCancel}><Trash2 className="h-4 w-4" /></Button>
        </div>

        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] tracking-widest uppercase font-semibold text-slate-500">Supplier</label>
            <select
              data-testid="manual-supplier"
              value={meta.supplier_name}
              onChange={(e) => setMeta({ ...meta, supplier_name: e.target.value })}
              className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm bg-white font-mono"
            >
              <option value="">Select supplier…</option>
              {activeSuppliers.map((s) => (
                <option key={s.id} value={s.name} data-testid={`manual-supplier-opt-${s.name}`}>
                  {s.name}
                </option>
              ))}
            </select>
            {activeSuppliers.length === 0 && (
              <p className="text-[10px] text-amber-700 mt-1">No suppliers in master. Add one under Masters → Suppliers.</p>
            )}
          </div>
          <div>
            <label className="text-[10px] tracking-widest uppercase font-semibold text-slate-500">Packaging list #</label>
            <Input data-testid="manual-pkg-no" value={meta.packaging_list_number} onChange={(e) => setMeta({ ...meta, packaging_list_number: e.target.value })} placeholder="e.g. INV-1234 or ANY unique ref" />
          </div>
          <div>
            <label className="text-[10px] tracking-widest uppercase font-semibold text-slate-500">Vehicle #</label>
            <Input data-testid="manual-vehicle" value={meta.vehicle_number} onChange={(e) => setMeta({ ...meta, vehicle_number: e.target.value })} />
          </div>
          <div>
            <label className="text-[10px] tracking-widest uppercase font-semibold text-slate-500">Date of dispatch</label>
            <Input type="date" data-testid="manual-date" value={meta.date_of_dispatch} onChange={(e) => setMeta({ ...meta, date_of_dispatch: e.target.value })} />
          </div>
        </div>

        <div className="px-4 pb-3 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="text-[10px] tracking-widest uppercase text-slate-500">
                <TableHead className="w-8">#</TableHead>
                <TableHead>Name ({kind === "sfg" ? "SFG SKU" : "RM"})</TableHead>
                <TableHead>Grade</TableHead>
                <TableHead>Thick&nbsp;(mm)</TableHead>
                <TableHead>Width&nbsp;(mm)</TableHead>
                <TableHead>Net&nbsp;(kg)</TableHead>
                <TableHead>Gross&nbsp;(kg)</TableHead>
                <TableHead>Supplier coil #</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono text-[11px]">{i + 1}</TableCell>
                  <TableCell>
                    <select
                      data-testid={`manual-name-${i}`}
                      value={r.material_id}
                      onChange={(e) => pickMaterial(i, e.target.value)}
                      className="h-9 px-2 border border-slate-300 rounded-sm text-sm bg-white font-mono w-full min-w-[140px]"
                    >
                      <option value="">Select…</option>
                      {activeMaterials.map((m) => (
                        <option key={m.id} value={m.id} data-testid={`manual-name-opt-${i}-${m.name}`}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </TableCell>
                  <TableCell><Input data-testid={`manual-grade-${i}`} value={r.grade} onChange={(e) => updRow(i, "grade", e.target.value)} placeholder="Q195" /></TableCell>
                  <TableCell><Input data-testid={`manual-thk-${i}`} type="number" step="0.01" value={r.thickness_mm} onChange={(e) => updRow(i, "thickness_mm", e.target.value)} /></TableCell>
                  <TableCell><Input data-testid={`manual-wid-${i}`} type="number" step="0.01" value={r.width_mm} onChange={(e) => updRow(i, "width_mm", e.target.value)} /></TableCell>
                  <TableCell><Input data-testid={`manual-net-${i}`} type="number" step="0.01" value={r.net_weight_kg} onChange={(e) => updRow(i, "net_weight_kg", e.target.value)} /></TableCell>
                  <TableCell><Input data-testid={`manual-gross-${i}`} type="number" step="0.01" value={r.gross_weight_kg} onChange={(e) => updRow(i, "gross_weight_kg", e.target.value)} /></TableCell>
                  <TableCell><Input data-testid={`manual-sup-${i}`} value={r.supplier_coil_id} onChange={(e) => updRow(i, "supplier_coil_id", e.target.value)} placeholder="optional" /></TableCell>
                  <TableCell>
                    {rows.length > 1 && (
                      <button data-testid={`manual-rm-${i}`} type="button" onClick={() => rmRow(i)} className="text-red-700 hover:text-red-900"><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {activeMaterials.length === 0 && (
            <p className="text-[10px] text-amber-700 mt-1">
              No {kind === "sfg" ? "SFG SKUs" : "RM grades"} in master. Add one under Masters → {kind === "sfg" ? "SFG SKU" : "RM Grade"}.
            </p>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-between">
          <Button data-testid="manual-add-row" variant="outline" onClick={addRow}><Plus className="h-4 w-4 mr-1.5" /> Add coil row</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button data-testid="manual-save" disabled={!canSave} onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white">
              Add to review list
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackagingLists;
